import { useState } from 'react';

import { Alert, Button, Card, CardHeader, Checkbox, Input, Select } from '@/components/ui';

import { HANDOVER_STAGES } from './stages';

/**
 * Connect a system, and choose what it receives.
 *
 * The credential inputs are rendered from what the connector said it needs,
 * not from a form written per vendor — so adding Greenhouse means adding a
 * connector, not another screen.
 *
 * Two things this form is careful about. A stored credential is never shown,
 * because it is never sent back; leaving the box empty on an existing
 * connection means "keep the one you have", and the placeholder says so. And
 * automatic handover is disabled rather than hidden when the plan does not
 * cover it, with the reason next to it — a control that vanishes leaves
 * somebody wondering whether the feature exists at all.
 */
export function ConnectionForm({
  providers,
  connection,
  defaultProvider,
  automationAllowed,
  automationNote,
  planName,
  onSave,
  isSaving,
}) {
  const [providerName, setProviderName] = useState(
    connection?.provider ?? defaultProvider ?? providers[0]?.name,
  );
  const [credential, setCredential] = useState('');
  const [options, setOptions] = useState(connection?.options ?? {});
  const [fromStage, setFromStage] = useState(connection?.from_stage ?? 'SHORTLISTED');
  const [autoExport, setAutoExport] = useState(connection?.auto_export ?? false);

  const provider = providers.find((item) => item.name === providerName);
  const isSameProvider = connection?.provider === providerName;
  const secretFields = (provider?.fields ?? []).filter((field) => field.secret);
  const plainFields = (provider?.fields ?? []).filter((field) => !field.secret);

  function submit(event) {
    event.preventDefault();
    onSave({
      provider: providerName,
      // Omitted rather than sent empty: the API reads "absent" as "keep what
      // you have", and an empty string would read as "there is none".
      ...(credential ? { credential } : {}),
      options,
      from_stage: fromStage,
      auto_export: autoExport,
    });
  }

  return (
    <Card>
      <CardHeader
        title={connection ? 'Your integration' : 'Connect your ATS'}
        description="Choose where your candidates go, and at what point they go there."
      />
      <form className="space-y-5" onSubmit={submit}>
        <Select
          label="System"
          value={providerName}
          onChange={(event) => {
            setProviderName(event.target.value);
            setCredential('');
          }}
        >
          {providers.map((item) => (
            <option key={item.name} value={item.name}>
              {item.label}
            </option>
          ))}
        </Select>
        {provider?.description && (
          <p className="text-ink-muted -mt-2 text-sm">{provider.description}</p>
        )}

        {plainFields.map((field) => (
          <Input
            key={field.name}
            label={field.label}
            hint={field.help_text ?? undefined}
            required={field.required}
            value={options[field.name] ?? ''}
            onChange={(event) =>
              setOptions((current) => ({ ...current, [field.name]: event.target.value }))
            }
          />
        ))}

        {secretFields.map((field) => (
          <Input
            key={field.name}
            type="password"
            label={field.label}
            autoComplete="off"
            hint={
              isSameProvider && connection?.credential_hint
                ? `Stored, ending ${connection.credential_hint}. Leave blank to keep it.`
                : (field.help_text ?? undefined)
            }
            placeholder={isSameProvider && connection ? 'Unchanged' : ''}
            value={credential}
            onChange={(event) => setCredential(event.target.value)}
          />
        ))}

        <Select
          label="Hand candidates over"
          hint="Nobody who has been rejected or withdrawn is ever sent, whatever stage they reached."
          value={fromStage}
          onChange={(event) => setFromStage(event.target.value)}
        >
          {HANDOVER_STAGES.map((stage) => (
            <option key={stage.value} value={stage.value}>
              {stage.label}
            </option>
          ))}
        </Select>

        <div>
          <Checkbox
            label="Send them automatically, without me asking"
            checked={autoExport}
            disabled={!automationAllowed}
            onChange={(event) => setAutoExport(event.target.checked)}
          />
          {!automationAllowed && (
            <Alert tone="info" className="mt-3">
              {automationNote} Your {planName} plan does not include it.
            </Alert>
          )}
        </div>

        <Button type="submit" isLoading={isSaving} disabled={!provider}>
          {connection ? 'Save changes' : 'Connect'}
        </Button>
      </form>
    </Card>
  );
}
